import { useState, useEffect, useCallback } from 'react';
import './pages.css';
import './SupportPage.css';
import { useBrand } from '../context/BrandContext';

const TICKET_STATUS = {
    Open: { cls: 'pg-badge-amber', icon: '🟡', label: 'Open' },
    In_Progress: { cls: 'pg-badge-blue', icon: '🔄', label: 'In Progress' },
    Resolved: { cls: 'pg-badge-green', icon: '✅', label: 'Resolved' },
    Closed: { cls: 'pg-badge-neutral', icon: '🔒', label: 'Closed' },
};
const CATEGORIES = [
    { key: 'Loan_Status', label: 'Loan Inquiry', icon: '💰' },
    { key: 'EMI', label: 'EMI & Payments', icon: '💳' },
    { key: 'Document', label: 'KYC & Documents', icon: '🆔' },
    { key: 'General', label: 'General Support', icon: '💬' },
    { key: 'Fraud', label: 'Fraud Report', icon: '🚨' },
    { key: 'Other', label: 'Other', icon: '📝' },
];

const CREATE_TICKET_CATEGORIES = [
    { value: 'Loan_Status', label: 'Loan Inquiry', icon: '💰' },
    { value: 'EMI', label: 'EMI & Payments', icon: '💳' },
    { value: 'Document', label: 'KYC & Documents', icon: '🆔' },
    { value: 'General', label: 'Account Access', icon: '👤' },
    { value: 'General', label: 'General Support', icon: '💬' },
    { value: 'Fraud', label: 'Fraud Report', icon: '🚨' },
    { value: 'Other', label: 'Other', icon: '📝' },
];

const CATEGORY_META = Object.fromEntries(CATEGORIES.map((category) => [category.key, category]));

function getCategoryMeta(category) {
    return CATEGORY_META[category] || { label: category || 'General', icon: '💬' };
}

function getStatusMeta(status) {
    return TICKET_STATUS[status] || { cls: 'pg-badge-neutral', icon: '?', label: status || 'Unknown' };
}


function SupportPage() {
    const { supportEmail, supportPhone, whatsappDisplay } = useBrand();
    const CONTACT = [
        { icon: '📞', label: 'Call Us', detail: supportPhone, sub: 'Mon–Sat, 9 AM – 6 PM' },
        { icon: '✉️', label: 'Email Us', detail: supportEmail, sub: 'Reply within 24h' },
        { icon: '💬', label: 'Live Chat', detail: 'Chat with us', sub: 'Available now' },
        { icon: '📱', label: 'WhatsApp', detail: whatsappDisplay, sub: 'Fast response' },
    ];
    const [tickets, setTickets] = useState([]);
    const [selected, setSelected] = useState(null);
    const [replies, setReplies] = useState([]);
    const [newReply, setNewReply] = useState('');
    const [sending, setSending] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [tab, setTab] = useState('tickets');
    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchTickets = useCallback(() => {
        const s = JSON.parse(localStorage.getItem('userSession'));
        if (!s?.userId) return;
        fetch(`${API_URL}/api/support/tickets/${s.userId}`).then(r => r.json())
            .then(d => {
                if (d.success) setTickets(d.tickets.map(t => ({
                    id: t.id, subject: t.subject, category: t.category,
                    description: t.description, status: t.status, priority: t.priority,
                    created: new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
                    updated: new Date(t.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
                })));
            }).catch(() => { });
    }, [API_URL]);

    const fetchReplies = useCallback((tid) => {
        fetch(`${API_URL}/api/support/tickets/${tid}/replies`).then(r => r.json())
            .then(d => { if (d.success) setReplies(d.replies); }).catch(() => { });
    }, [API_URL]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    const handleCreate = async (e) => {
        e.preventDefault();
        const s = JSON.parse(localStorage.getItem('userSession'));
        const f = e.target;
        try {
            const r = await fetch(`${API_URL}/api/support/create-ticket`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: s.userId, category: f.category.value, subject: f.subject.value, description: f.description.value }),
            });
            const d = await r.json();
            if (d.success) { setShowCreate(false); fetchTickets(); }
        } catch { }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!newReply.trim() || sending) return;
        const s = JSON.parse(localStorage.getItem('userSession'));
        setSending(true);
        try {
            const r = await fetch(`${API_URL}/api/support/tickets/${selected.id}/reply`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: s.userId, message: newReply, isAdminReply: false }),
            });
            const d = await r.json();
            if (d.success) { setNewReply(''); fetchReplies(selected.id); }
        } catch { } finally { setSending(false); }
    };

    const stats = {
        total: tickets.length,
        open: tickets.filter(t => t.status === 'Open' || t.status === 'In_Progress').length,
        resolved: tickets.filter(t => t.status === 'Resolved' || t.status === 'Closed').length,
    };

    return (
        <div className="pg-root sp-root">
            <section className="sp-hero">
                <div className="sp-hero-copy">
                    <span className="sp-hero-kicker"><i aria-hidden="true">🎫</i>Support Helpdesk</span>
                    <h1>Need Help?<span>We’re Just a Message Away.</span></h1>
                    <p>Raise a request, track your tickets, or get in touch with our support team.</p>
                </div>
                <div className="sp-hero-art" aria-hidden="true">
                    {/* <span className="sp-art-agent">
                        <span className="sp-art-face">🧑‍💼</span>
                        <span className="sp-art-headset">🎧</span>
                    </span>
                    <span className="sp-art-bubble sp-art-bubble-1"><b /><b /><b /></span>
                    <span className="sp-art-bubble sp-art-bubble-2"><b /><b /></span>
                    <span className="sp-art-chip sp-art-clock">🕒</span>
                    <span className="sp-art-chip sp-art-mail">✉️</span>
                    <span className="sp-art-chip sp-art-help">?</span> */}
                </div>
                <div className="sp-hero-trust">
                    <div><span className="tone-cyan" aria-hidden="true">⏱️</span><p><strong>Fast Response</strong><small>We usually respond within 24 hours</small></p></div>
                    <div><span className="tone-violet" aria-hidden="true">👩‍💼</span><p><strong>Expert Support</strong><small>Get help from our loan specialists</small></p></div>
                    <div><span className="tone-amber" aria-hidden="true">🛡️</span><p><strong>Secure &amp; Private</strong><small>Your data is always safe with us</small></p></div>
                </div>
            </section>

            <div className="sp-stats">
                {[['🎫', 'Total Tickets', stats.total, 'blue', '🗂️'], ['🕒', 'Open / Active', stats.open, 'amber', '⏳'], ['✅', 'Resolved', stats.resolved, 'green', '📋']].map(([ic, lb, v, tone, art]) => (
                    <div key={lb} className={`sp-stat tone-${tone}`}>
                        <span className="sp-stat-icon" aria-hidden="true">{ic}</span>
                        <span className="sp-stat-text">
                            <span className="sp-stat-label">{lb}</span>
                            <span className="sp-stat-value">{v}</span>
                        </span>
                        <span className="sp-stat-art" aria-hidden="true">{art}</span>
                    </div>
                ))}
            </div>

            <div className="sp-toolbar">
                <div className="sp-tabs" role="tablist">
                    {[['tickets', '🎫', 'My Tickets'], ['contact', '📞', 'Contact Us']].map(([k, ic, l]) => (
                        <button key={k} role="tab" aria-selected={tab === k} className={`sp-tab ${tab === k ? 'is-active' : ''}`} onClick={() => setTab(k)}>
                            <span aria-hidden="true">{ic}</span>{l}
                        </button>
                    ))}
                </div>
                <button className="sp-new-btn" onClick={() => setShowCreate(true)}><span aria-hidden="true">＋</span> New Request</button>
            </div>

            {tab === 'tickets' && (
                <div className="sp-panel">
                    <div className="sp-panel-head">
                        <span className="sp-panel-icon" aria-hidden="true">🛡️</span>
                        <div>
                            <h2>Support Tickets</h2>
                            <p>View and manage all your support requests in one place.</p>
                        </div>
                        <span className="sp-count">{tickets.length} total</span>
                    </div>
                    {tickets.length === 0 ? (
                        <div className="sp-empty">
                            <div className="sp-empty-bg" aria-hidden="true">
                                {/* <span className="sp-empty-chip sp-empty-chip-1">📄</span>
                                <span className="sp-empty-chip sp-empty-chip-2">🎧</span>
                                <span className="sp-empty-chip sp-empty-chip-3">?</span> */}
                            </div>
                            <div className="sp-empty-art" aria-hidden="true">
                                <span className="sp-empty-box">📦</span>
                                <span className="sp-empty-dots"><b /><b /><b /></span>
                                <span className="sp-empty-plane">✈</span>
                                <span className="sp-empty-spark s1">✦</span>
                                <span className="sp-empty-spark s2">✦</span>
                                <span className="sp-empty-spark s3">✦</span>
                            </div>
                            <h3>No tickets yet</h3>
                            <p>Create a new support request and our team will assist you shortly.</p>
                            <button className="sp-empty-btn" onClick={() => setShowCreate(true)}>Create Ticket <span aria-hidden="true">→</span></button>
                        </div>
                    ) : (
                        <div className="pg-table-wrap">
                            <table className="pg-table">
                                <thead><tr><th>Ticket ID</th><th>Category</th><th>Subject</th><th>Updated</th><th>Status</th><th></th></tr></thead>
                                <tbody>
                                    {tickets.map(t => {
                                        const st = getStatusMeta(t.status);
                                        const categoryMeta = getCategoryMeta(t.category);
                                        return (
                                            <tr key={t.id}>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: '700', color: '#0755d9' }}>#TIC{t.id.toString().padStart(4, '0')}</td>
                                                <td>{categoryMeta.icon} {categoryMeta.label}</td>
                                                <td style={{ fontWeight: '600', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</td>
                                                <td style={{ fontSize: '0.8rem' }}>{t.updated}</td>
                                                <td><span className={`pg-badge ${st.cls}`}>{st.icon} {st.label}</span></td>
                                                <td>
                                                    <button className="pg-btn pg-btn-ghost" style={{ padding: '0.3rem 0.7rem', fontSize: '0.75rem' }} onClick={() => { setSelected(t); fetchReplies(t.id); }}>
                                                        View & Reply
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {tab === 'contact' && (
                <div className="sp-panel">
                    <div className="sp-panel-head">
                        <span className="sp-panel-icon" aria-hidden="true">📞</span>
                        <div>
                            <h2>Get in Touch</h2>
                            <p>Reach our support team through any of these channels.</p>
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '0.85rem' }}>
                        {CONTACT.map(c => (
                            <div key={c.label} className="sp-contact-card" style={{ background: '#f4f9ff', border: '1px solid #cfe4fa', borderRadius: '16px', padding: '1.25rem', textAlign: 'center', cursor: 'pointer', transition: 'all 0.18s' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#eaf4ff'} onMouseLeave={e => e.currentTarget.style.background = '#f4f9ff'}>
                                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{c.icon}</div>
                                <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b', marginBottom: '0.2rem' }}>{c.label}</div>
                                <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#0755d9' }}>{c.detail}</div>
                                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.2rem' }}>{c.sub}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: '#f4f9ff', border: '1px solid #cfe4fa', borderRadius: '16px' }}>
                        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>📋 FAQs</h3>
                        {[['How do I apply for a loan?', 'Click "Apply for Loan" in the sidebar and follow the 5-step journey.'],
                        ['When is my EMI due?', 'Your EMI due date is shown in the Payments section.'],
                        ['How long does KYC take?', 'KYC verification typically takes 1-2 business days.'],
                        // ['Can I redeem my reward points?', 'Yes! Your earned points can be applied as a discount during repayment.'],
                        ].map(([q, a]) => (
                            <details key={q} style={{ borderBottom: '1px solid #dcecff', padding: '0.6rem 0' }}>
                                <summary style={{ fontSize: '0.84rem', fontWeight: '700', cursor: 'pointer', color: '#1e293b', padding: '0.2rem 0' }}>{q}</summary>
                                <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0.4rem 0 0', paddingLeft: '0.5rem', lineHeight: 1.6 }}>{a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            )}

            {/* Ticket Thread Modal */}
            {selected && (
                <div className="pg-modal-overlay" onClick={() => setSelected(null)}>
                    <div className="pg-modal pg-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="pg-modal-header">
                            <div>
                                <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.2rem' }}>
                                    #TIC{selected.id.toString().padStart(4, '0')} · {getCategoryMeta(selected.category).label}
                                </div>
                                <h2 style={{ fontSize: '1rem' }}>{selected.subject}</h2>
                            </div>
                            <button className="pg-modal-close" onClick={() => setSelected(null)}>×</button>
                        </div>
                        <div className="pg-modal-body">
                            <div style={{ maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem', background: '#f4f9ff', borderRadius: '12px', marginBottom: '1rem' }}>
                                <div style={{ alignSelf: 'flex-start', maxWidth: '85%', padding: '0.8rem 1rem', background: '#0755d9', color: 'white', borderRadius: '14px 14px 14px 4px', fontSize: '0.88rem' }}>
                                    <p style={{ margin: 0 }}>{selected.description}</p>
                                    <span style={{ fontSize: '0.65rem', opacity: 0.7, marginTop: '4px', display: 'block' }}>{selected.created} (Original)</span>
                                </div>
                                {replies.map(r => (
                                    <div key={r.id} style={{
                                        alignSelf: r.is_admin_reply ? 'flex-end' : 'flex-start', maxWidth: '85%',
                                        padding: '0.8rem 1rem',
                                        background: r.is_admin_reply ? '#eaf4ff' : '#d8ebff',
                                        color: '#1e1b4b',
                                        borderRadius: r.is_admin_reply ? '14px 14px 4px 14px' : '14px 14px 14px 4px', fontSize: '0.88rem'
                                    }}>
                                        <p style={{ margin: 0 }}>{r.message}</p>
                                        <span style={{ fontSize: '0.65rem', opacity: 0.6, marginTop: '4px', display: 'block' }}>
                                            {r.is_admin_reply ? 'Support Team' : 'You'} · {new Date(r.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {selected.status !== 'Closed' && selected.status !== 'Resolved' ? (
                                <form onSubmit={handleReply} style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input value={newReply} onChange={e => setNewReply(e.target.value)} placeholder="Type your reply…" disabled={sending}
                                        style={{ flex: 1, padding: '0.65rem 1rem', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit' }} />
                                    <button type="submit" className="pg-btn pg-btn-primary" disabled={sending}>{sending ? '…' : 'Send'}</button>
                                </form>
                            ) : (
                                <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>This ticket is {selected.status.toLowerCase()} and cannot receive replies.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create Ticket Modal */}
            {showCreate && (
                <div className="pg-modal-overlay" onClick={() => setShowCreate(false)}>
                    <div className="pg-modal" onClick={e => e.stopPropagation()}>
                        <div className="pg-modal-header">
                            <h2>New Support Request</h2>
                            <button className="pg-modal-close" onClick={() => setShowCreate(false)}>×</button>
                        </div>
                        <form onSubmit={handleCreate}>
                            <div className="pg-modal-body">
                                <div className="pg-form">
                                    <div className="pg-field">
                                        <label>Category *</label>
                                        <select name="category" required>
                                            <option value="">Select a category</option>
                                            {CREATE_TICKET_CATEGORIES.map((category, index) => (
                                                <option key={`${category.value}-${index}`} value={category.value}>
                                                    {category.icon} {category.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="pg-field">
                                        <label>Subject *</label>
                                        <input name="subject" type="text" required placeholder="Brief summary of your issue" />
                                    </div>
                                    <div className="pg-field">
                                        <label>Description *</label>
                                        <textarea name="description" rows="4" required placeholder="Describe your issue in detail…" />
                                    </div>
                                </div>
                            </div>
                            <div className="pg-modal-footer">
                                <button type="button" className="pg-btn pg-btn-ghost" onClick={() => setShowCreate(false)}>Discard</button>
                                <button type="submit" className="pg-btn pg-btn-primary">Submit Request</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SupportPage;
